import type { Environment } from 'types/environment'

import type { Storefront } from 'types/models/storefront'
import type { Locale } from '~/types/models/market'
import type { marketCode } from '~/types/storyblok/blocks/all-markets'
import { getGeneratedStorefronts } from '#shared/utils/storefront'
import { getAllMarkets } from 'lib/storefronts'

export const useStorefrontStore = defineStore('StorefrontStore', () => {
  const all = ref(getGeneratedStorefronts())
  const current = ref<Storefront>(getGeneratedStorefronts()[0] as Storefront)
  const currentMarketCountryCode = ref<Environment.CountryCode>('DE')
  const currentCountry = ref<Environment.CountryCode | undefined>()

  const allMarkets = computed(() => {
    return getAllMarkets().sort((a, b) => a.name.english.localeCompare(b.name.english))
  })
  const currentStorefrontCode = computed(() => current.value.storefrontCode)
  const currentMarket = computed(
    () =>
      allMarkets.value.find(market => market.countryCode === currentMarketCountryCode.value)
      || current.value.markets[0]!,
  )
  const currentLocale = computed<Locale>(() => {
    return currentMarket.value.locale
  })
  const currency = computed(() => currentMarket.value.currency)
  const contentKey = computed(() => current.value.contentKey)
  const language = computed(() => currentMarket.value.language)
  const freeShippingThreshold = computed(() => {
    const storyblokStore = useStoryblokStore()
    const threshold = storyblokStore.state.checkoutFreeShippingBar?.freeShippingThreshold[currentMarketCountryCode.value]
    return Number.isNaN(Number(threshold)) ? 0 : Number.parseInt(threshold ?? '0')
  })

  const setCurrentMarket = (
    countryCode: Environment.CountryCode,
  ) => {
    currentMarketCountryCode.value = countryCode
  }

  const setCurrentStorefront = (storefront: Storefront) => {
    current.value = storefront
  }

  const setCurrentCountry = (countryCode: Environment.CountryCode) => {
    currentCountry.value = countryCode
  }

  return {
    all,
    current,
    currentCountry,
    currentMarketCountryCode,
    allMarkets,
    currentStorefrontCode,
    currency,
    currentMarket,
    currentLocale,
    contentKey,
    language,
    freeShippingThreshold,

    setCurrentMarket,
    setCurrentStorefront,
    setCurrentCountry,
  }
})
